/**
 * Haptic feedback. Wraps `navigator.vibrate` with named patterns for the
 * common events. No-op on devices without the API (most desktop browsers,
 * iOS Safari prior to ≥iOS 16 in PWA mode).
 *
 * Patterns are deliberately short — anything > 30ms feels like the phone
 * is "buzzing at me" instead of confirming a tap. Source of magic numbers:
 * Material Design haptic guidance + Apple HIG (which Apple won't admit
 * works in PWAs, but it does on Android via vibrate()).
 */

export type HapticKind = 'tap' | 'correct' | 'wrong' | 'complete' | 'star_lit';

const PATTERNS: Record<HapticKind, number | number[]> = {
  // Soft confirmation — single brief blip
  tap: 8,
  // Pleasant double-tick — "yes!"
  correct: [10, 40, 10],
  // Harder single thud — "wrong"
  wrong: 30,
  // Celebratory triple-tick — lesson done
  complete: [15, 50, 15, 50, 25],
  // Skill-tree star light-up — same as a tap, the visual carries the moment
  star_lit: 6,
};

let supported: boolean | null = null;

function check(): boolean {
  if (supported !== null) return supported;
  supported =
    typeof navigator !== 'undefined' &&
    typeof navigator.vibrate === 'function' &&
    // navigator.vibrate exists on iOS Safari but is a no-op (Apple disabled it).
    // We don't have a reliable feature-detect, so we just call it — safe
    // either way. The cache here is just a perf micro-optimization.
    true;
  return supported;
}

export function haptic(kind: HapticKind): void {
  if (!check()) return;
  try {
    navigator.vibrate(PATTERNS[kind]);
  } catch {
    // Some embedded WebViews (in-app browsers) throw on vibrate — swallow.
  }
}

/** Cancel any in-progress vibration. Call on route change so we don't carry
 *  haptic into the next screen. */
export function stopHaptic(): void {
  if (!check()) return;
  try {
    navigator.vibrate(0);
  } catch {
    /* noop */
  }
}
